// HTTP API client. In the web app the API is same-origin and authenticated with an
// httpOnly cookie. In the iOS app (Capacitor) the API is on another origin, where
// WKWebView blocks third-party cookies – there we use a bearer token instead.

export interface User {
  id: string;
  username: string;
}

export interface SessionInfo {
  id: string;
  state: string;
  dpr: number;
  iceServers: RTCIceServer[];
}

export interface SitePermission {
  origin: string;
  kind: string;
  allow: boolean;
  updated_at: number;
}

export interface Credential {
  id: string;
  origin: string;
  username: string;
  created_at: number;
  updated_at: number;
}

export interface SiteDataRow {
  host: string;
  cookies: number;
  passwords: number;
  permissions: number;
}

export class ApiError extends Error {
  constructor(message: string, public status: number) {
    super(message);
  }
}

const TOKEN_KEY = 'gcb.token';
const SERVER_KEY = 'gcb.server';

const store = {
  get: (k: string) => {
    try {
      return localStorage.getItem(k);
    } catch {
      return null;
    }
  },
  set: (k: string, v: string | null) => {
    try {
      if (v === null) localStorage.removeItem(k);
      else localStorage.setItem(k, v);
    } catch {
      /* private mode */
    }
  },
};

export class Api {
  /** '' = same origin (web). For the iOS app this is e.g. https://cloud.example.com */
  base: string;
  token: string | null;

  constructor(base?: string) {
    this.base = (base ?? store.get(SERVER_KEY) ?? '').replace(/\/+$/, '');
    this.token = store.get(TOKEN_KEY);
  }

  get crossOrigin(): boolean {
    if (!this.base) return false;
    try {
      return new URL(this.base).origin !== location.origin;
    } catch {
      return true;
    }
  }

  setServer(url: string) {
    this.base = url.trim().replace(/\/+$/, '');
    store.set(SERVER_KEY, this.base || null);
  }

  url(path: string) {
    return this.base + path;
  }

  wsUrl(path: string) {
    const u = new URL(this.url(path), location.href);
    u.protocol = u.protocol === 'https:' ? 'wss:' : 'ws:';
    return u.toString();
  }

  wsProtocols(): string[] {
    return this.token && this.crossOrigin ? ['gcb', `auth.${this.token}`] : ['gcb'];
  }

  authHeaders(): Record<string, string> {
    const h: Record<string, string> = { 'x-gcb-csrf': '1' };
    if (this.token && this.crossOrigin) h.authorization = `Bearer ${this.token}`;
    return h;
  }

  async req<T>(method: string, path: string, body?: unknown): Promise<T> {
    const res = await fetch(this.url(path), {
      method,
      credentials: this.crossOrigin ? 'omit' : 'same-origin',
      headers: { ...this.authHeaders(), ...(body !== undefined ? { 'content-type': 'application/json' } : {}) },
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new ApiError(data.error ?? res.statusText, res.status);
    return data as T;
  }

  config() {
    return this.req<{ allowRegistration: boolean; licenseRequired?: boolean }>('GET', '/api/config');
  }

  async me(): Promise<User | null> {
    try {
      return (await this.req<{ user: User }>('GET', '/api/auth/me')).user;
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) return null;
      throw e;
    }
  }

  async login(username: string, password: string, register = false, licenseKey?: string): Promise<User> {
    const r = await this.req<{ user: User; token: string }>('POST', register ? '/api/auth/register' : '/api/auth/login', register ? { username, password, licenseKey } : { username, password });
    // The token is only kept when cookies can't be used (cross-origin / native app).
    this.token = this.crossOrigin ? r.token : null;
    store.set(TOKEN_KEY, this.token);
    return r.user;
  }

  async logout() {
    await this.req('POST', '/api/auth/logout').catch(() => {});
    this.token = null;
    store.set(TOKEN_KEY, null);
  }

  createSession(o: { width: number; height: number; dpr: number; lang: string }) {
    return this.req<SessionInfo>('POST', '/api/sessions', o);
  }

  endSession(id: string) {
    return this.req('DELETE', `/api/sessions/${id}`);
  }

  async permissions(): Promise<SitePermission[]> {
    return (await this.req<{ permissions: SitePermission[] }>('GET', '/api/permissions')).permissions;
  }

  setPermission(origin: string, kind: string, allow: boolean) {
    return this.req('PUT', '/api/permissions', { origin, kind, allow });
  }

  deletePermission(origin: string, kind?: string) {
    const q = new URLSearchParams({ origin, ...(kind ? { kind } : {}) });
    return this.req('DELETE', `/api/permissions?${q}`);
  }

  async passwords(origin?: string): Promise<{ passwords: Credential[]; neverSave: string[] | boolean }> {
    return this.req('GET', '/api/passwords' + (origin ? '?origin=' + encodeURIComponent(origin) : ''));
  }

  async revealPassword(id: string): Promise<string> {
    return (await this.req<{ password: string }>('GET', '/api/passwords/' + encodeURIComponent(id))).password;
  }

  savePassword(origin: string, username: string, password: string) {
    return this.req('PUT', '/api/passwords', { origin, username, password });
  }

  deletePassword(id: string) {
    return this.req('DELETE', '/api/passwords/' + encodeURIComponent(id));
  }

  setNeverSave(origin: string, on: boolean) {
    return this.req('PUT', '/api/passwords-never', { origin, on });
  }

  async siteData(): Promise<SiteDataRow[]> {
    return (await this.req<{ sites: SiteDataRow[] }>('GET', '/api/site-data')).sites;
  }

  deleteSiteData(o: { host?: string; all?: boolean; permissions?: boolean; passwords?: boolean }) {
    const q = new URLSearchParams();
    if (o.all) q.set('all', '1');
    if (o.host) q.set('host', o.host);
    if (o.permissions) q.set('permissions', '1');
    if (o.passwords) q.set('passwords', '1');
    return this.req('DELETE', '/api/site-data?' + q);
  }

  changePassword(current: string, next: string) {
    return this.req('POST', '/api/auth/password', { current, next });
  }

  /** Upload files for a pending remote <input type=file>, with progress. */
  upload(sessionId: string, chooserId: number, files: File[], onProgress?: (f: number) => void): Promise<void> {
    return new Promise((resolve, reject) => {
      const fd = new FormData();
      for (const f of files) fd.append('file', f, f.name);
      const xhr = new XMLHttpRequest();
      xhr.open('POST', this.url(`/api/sessions/${sessionId}/upload?chooser=${chooserId}`));
      xhr.withCredentials = !this.crossOrigin;
      for (const [k, v] of Object.entries(this.authHeaders())) xhr.setRequestHeader(k, v);
      xhr.upload.onprogress = (e) => e.lengthComputable && onProgress?.(e.loaded / e.total);
      xhr.onload = () => (xhr.status < 300 ? resolve() : reject(new ApiError(xhr.responseText || 'upload failed', xhr.status)));
      xhr.onerror = () => reject(new ApiError('upload failed', 0));
      xhr.send(fd);
    });
  }

  downloadUrl(sessionId: string, guid: string) {
    return this.url(`/api/sessions/${sessionId}/downloads/${encodeURIComponent(guid)}`);
  }

  async downloadBlob(sessionId: string, guid: string): Promise<Blob> {
    const res = await fetch(this.downloadUrl(sessionId, guid), { headers: this.authHeaders(), credentials: this.crossOrigin ? 'omit' : 'same-origin' });
    if (!res.ok) throw new ApiError('download failed', res.status);
    return res.blob();
  }
}
